/**
 * Owner: Masa Bayu
 * File: qxcircle.service.ts
 * Date:Time: 2026-09-18 10:42
 * Description: QXCircle engine core — feed/post/like.
 */

import {
  BadRequestException,
  ForbiddenException,
  Injectable,
  NotFoundException,
} from "./_nestjs-common";
import { seedQxCircleComments } from "./qxcircle-comment-seeds";
import { seedQxCircleFeed } from "./qxcircle-feed-seeds";
import { seedQxCircleNotifications } from "./qxcircle-notification-seeds";
import { normalizeQxCircleMedia } from "./qxcircle-media";
import { buildQxCircleTagFeed } from "./qxcircle-hashtag";
import {
  buildMentionNotificationsForPost,
  parseQxCircleFeedFilter,
} from "./qxcircle-service-helpers";
import { buildQxCircleTrendingTags } from "./qxcircle-trending";
import { buildQxCircleMentionFeed } from "./qxcircle-mention";
import {
  buildQxCircleBlocksList,
  filterQxCircleVisiblePosts,
  toggleQxCircleBlockAuthor,
} from "./qxcircle-block";
import {
  buildQxCircleMutesList,
  filterQxCircleFeedMutes,
  toggleQxCircleMuteAuthor,
} from "./qxcircle-mute";
import { shareQxCirclePostRecord } from "./qxcircle-share-action";
import { emptyQxCircleDraft, saveQxCircleDraft } from "./qxcircle-draft";
import { applyQxCirclePostUpdate } from "./qxcircle-post-update";
import { toggleQxCircleLike } from "./qxcircle-like-action";
import { buildQxCircleBookmarksList } from "./qxcircle-bookmark-list";
import {
  isManagedQxCirclePost,
  presentQxCirclePost,
} from "./qxcircle-post-presenter";
import type { AccountBundle } from "../account/account-types";
import { qxCircleAuthorFromAccount } from "./qxcircle-account";
import type {
  CreateQxCirclePostInput,
  QxCircleComment,
  QxCircleFeedPost,
  QxCircleFeedResponse,
  QxCircleModerationItem,
  QxCircleNotification,
  QxCircleOverview,
  QxCircleBookmarksResponse,
  QxCircleComposeDraft,
  QxCircleBlockResult,
  QxCircleBlocksResponse,
  QxCircleMuteResult,
  QxCircleMutesResponse,
  QxCircleMentionFeedResponse,
  QxCirclePostDetailResponse,
  QxCircleShareResult,
  QxCircleTagFeedResponse,
  QxCircleTrendingTagsResponse,
  UpdateQxCirclePostInput,
} from "./qxcircle.types";
import { QXCIRCLE_POST_BODY_MAX } from "./qxcircle.types";

@Injectable()
export class QxCircleServiceBase {
  protected readonly posts: QxCircleFeedPost[] = seedQxCircleFeed();
  protected readonly comments: QxCircleComment[] = seedQxCircleComments();
  protected readonly localLikes = new Set<string>();
  protected readonly localBookmarks = new Set<string>();
  protected readonly localFollows = new Set<string>();
  protected readonly localBlocks = new Set<string>();
  protected readonly localMutes = new Set<string>();
  protected readonly hiddenPosts = new Set<string>();
  protected readonly reports: QxCircleModerationItem[] = [];
  protected notifications: QxCircleNotification[] = seedQxCircleNotifications();
  protected draft: QxCircleComposeDraft = emptyQxCircleDraft();
  protected postSeq = 0;
  protected commentSeq = 0;
  protected ntfSeq = 0;
  protected reportSeq = 0;

  overview(): QxCircleOverview {
    return {
      world: "qxcircle",
      title: "QXCircle",
      tagline: "Connect · discover · share",
      feedCount: this.posts.length,
      status: "open",
      slice: "comment-edit",
    };
  }

  getPost(postId: string, accountId?: string | null): QxCirclePostDetailResponse {
    if (this.hiddenPosts.has(postId)) {
      throw new NotFoundException(`Post not found: ${postId}`);
    }
    const post = this.requirePost(postId);
    if (this.localBlocks.has(post.authorHandle)) {
      throw new NotFoundException(`Post not found: ${postId}`);
    }
    return {
      world: "qxcircle",
      slice: "post-detail",
      post: this.presentPost(post, accountId),
    };
  }

  feed(filterRaw?: string, accountId?: string | null): QxCircleFeedResponse {
    const filter = parseQxCircleFeedFilter(filterRaw);
    let visible = filterQxCircleVisiblePosts(
      this.posts,
      this.hiddenPosts,
      this.localBlocks,
    );
    visible = filterQxCircleFeedMutes(visible, this.localMutes);
    if (filter === "following") {
      visible = visible.filter(
        (post) =>
          isManagedQxCirclePost(post, accountId) ||
          this.localFollows.has(post.authorHandle),
      );
    }
    const items = visible
      .map((post) => this.presentPost(post, accountId))
      .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
    return {
      world: "qxcircle",
      slice: "following",
      filter,
      items,
    };
  }

  createPost(input: CreateQxCirclePostInput, account: AccountBundle): QxCircleFeedPost {
    const body = input.body?.trim() ?? "";
    if (body.length > QXCIRCLE_POST_BODY_MAX) {
      throw new BadRequestException(
        `Post body max ${QXCIRCLE_POST_BODY_MAX} characters`,
      );
    }
    if (!body && !input.media) {
      throw new BadRequestException("Post body or media is required");
    }
    const author = qxCircleAuthorFromAccount(account);
    this.postSeq += 1;
    const id = `qxcircle_post_local_${this.postSeq}`;
    const media = normalizeQxCircleMedia(input.media, id);
    const now = new Date().toISOString();
    const post: QxCircleFeedPost = {
      id,
      authorName: author.authorName,
      authorHandle: author.authorHandle,
      ownerAccountId: author.accountId,
      body,
      likeCount: 0,
      shareCount: 0,
      commentCount: 0,
      likedByLocal: false,
      savedByLocal: false,
      followedByLocal: false,
      mutedByLocal: false,
      canManage: true,
      media,
      createdAt: now,
      updatedAt: null,
    };
    this.posts.unshift(post);
    this.draft = emptyQxCircleDraft();
    const mentionNtf = buildMentionNotificationsForPost(
      this.ntfSeq,
      author.authorName,
      author.authorHandle,
      body,
      id,
    );
    this.ntfSeq = mentionNtf.nextSeq;
    if (mentionNtf.notifications.length > 0) {
      this.notifications.unshift(...mentionNtf.notifications);
    }
    return this.presentPost(post, author.accountId);
  }

  updatePost(
    postId: string,
    input: UpdateQxCirclePostInput,
    accountId: string,
  ): QxCircleFeedPost {
    const post = this.requireManagedPost(postId, accountId);
    applyQxCirclePostUpdate(post, input);
    return this.presentPost(post, accountId);
  }

  deletePost(postId: string, accountId: string): { ok: true; id: string } {
    this.requireManagedPost(postId, accountId);
    const idx = this.posts.findIndex((p) => p.id === postId);
    if (idx < 0) {
      throw new NotFoundException(`Post not found: ${postId}`);
    }
    this.posts.splice(idx, 1);
    for (let i = this.comments.length - 1; i >= 0; i -= 1) {
      if (this.comments[i]?.postId === postId) {
        this.comments.splice(i, 1);
      }
    }
    this.localLikes.delete(postId);
    this.localBookmarks.delete(postId);
    return { ok: true, id: postId };
  }

  likePost(postId: string): QxCircleFeedPost {
    const post = this.requirePost(postId);
    const toggled = toggleQxCircleLike(post, this.localLikes, this.ntfSeq);
    this.ntfSeq = toggled.nextSeq;
    if (toggled.notification) {
      this.notifications.unshift(toggled.notification);
    }
    return this.presentPost(post);
  }

  bookmarkPost(postId: string): QxCircleFeedPost {
    if (this.hiddenPosts.has(postId)) {
      throw new NotFoundException(`Post not found: ${postId}`);
    }
    const post = this.requirePost(postId);
    if (this.localBookmarks.has(postId)) {
      this.localBookmarks.delete(postId);
    } else {
      this.localBookmarks.add(postId);
    }
    return this.presentPost(post);
  }

  listBookmarks(): QxCircleBookmarksResponse {
    return buildQxCircleBookmarksList(
      this.posts, this.hiddenPosts, this.localBlocks, this.localBookmarks,
      (post) => this.presentPost(post),
    );
  }

  tagFeed(tagRaw: string): QxCircleTagFeedResponse {
    return buildQxCircleTagFeed(
      tagRaw,
      this.posts,
      this.hiddenPosts,
      this.localBlocks,
      (p) => this.presentPost(p),
    );
  }

  mentionFeed(handleRaw: string): QxCircleMentionFeedResponse {
    return buildQxCircleMentionFeed(
      handleRaw,
      this.posts,
      this.hiddenPosts,
      this.localBlocks,
      (p) => this.presentPost(p),
    );
  }

  trendingTags(): QxCircleTrendingTagsResponse {
    return buildQxCircleTrendingTags(
      this.posts,
      this.hiddenPosts,
      this.localBlocks,
    );
  }

  blockAuthor(
    handleRaw: string,
    viewerHandle: string | null = null,
  ): QxCircleBlockResult {
    const result = toggleQxCircleBlockAuthor(
      handleRaw,
      this.posts,
      this.localBlocks,
      this.localFollows,
      viewerHandle,
    );
    if (result.blockedByLocal) {
      this.localMutes.delete(result.authorHandle);
    }
    return result;
  }

  listBlocks(): QxCircleBlocksResponse {
    return buildQxCircleBlocksList(this.posts, this.localBlocks);
  }

  muteAuthor(
    handleRaw: string,
    viewerHandle: string | null = null,
  ): QxCircleMuteResult {
    return toggleQxCircleMuteAuthor(
      handleRaw,
      this.posts,
      this.localMutes,
      viewerHandle,
    );
  }

  listMutes(): QxCircleMutesResponse {
    return buildQxCircleMutesList(this.posts, this.localMutes);
  }

  sharePost(postId: string): QxCircleShareResult {
    return shareQxCirclePostRecord(
      this.requirePost(postId),
      typeof window !== "undefined" ? window.location.origin : "http://localhost:5173",
    );
  }

  getComposeDraft(): QxCircleComposeDraft {
    return this.draft;
  }

  saveComposeDraft(bodyRaw: string): QxCircleComposeDraft {
    return (this.draft = saveQxCircleDraft(bodyRaw));
  }

  clearComposeDraft(): QxCircleComposeDraft {
    return (this.draft = emptyQxCircleDraft());
  }

  protected requirePost(postId: string): QxCircleFeedPost {
    const post = this.posts.find((p) => p.id === postId);
    if (!post) {
      throw new NotFoundException(`Post not found: ${postId}`);
    }
    return post;
  }

  protected requireManagedPost(postId: string, accountId: string): QxCircleFeedPost {
    const post = this.requirePost(postId);
    if (!isManagedQxCirclePost(post, accountId)) {
      throw new ForbiddenException("Only your posts can be edited");
    }
    return post;
  }

  protected presentPost(
    post: QxCircleFeedPost,
    accountId?: string | null,
  ): QxCircleFeedPost {
    return presentQxCirclePost(
      post,
      this.comments,
      this.localLikes,
      this.localBookmarks,
      this.localFollows,
      this.localMutes,
      accountId,
    );
  }

}
